import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaTimes, FaUpload } from 'react-icons/fa';
import ButtonSpinner from '../components/ButtonSpinner';
import { useSelector } from 'react-redux';
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../constants';

const titleOptions = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Master', label: 'Master' },
  { value: 'Miss', label: 'Miss' },
];

const EditProfile = () => {
  const navigate = useNavigate();
  const { user: userInfo } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    title: userInfo?.title || '',
    firstName: userInfo?.firstname || '',
    middleName: userInfo?.middlename || '',
    lastName: userInfo?.lastname || '',
    email: userInfo?.email || '',
    phone: userInfo?.phone || '',
    alternative_phone: userInfo?.alternative_phone || '',
    emergency_phone: userInfo?.emergency_phone || '',
    current_address: userInfo?.current_address || '',
    qualification: userInfo?.qualification || '',
    experience: userInfo?.experience || '',
    profilePicture: null,
    password: '',
  });
  const [previewUrl, setPreviewUrl] = useState(userInfo?.profile_picture || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('File size should not exceed 2MB.');
        return;
      }
      setError('');
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    const requiredFields = ['title', 'firstName', 'lastName', 'email', 'phone', 'qualification', 'experience'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Please fill in the ${field.replace('_', ' ')} field.`);
        setIsSubmitting(false);
        return;
      }
    }

    // Upload to Cloudinary if new picture
    let imageUrl = null;
    if (formData.profilePicture) {
      const cloudData = new FormData();
      cloudData.append('file', formData.profilePicture);
      cloudData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      cloudData.append('folder', 'schportal/teachers');

      try {
        const cloudRes = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          { method: 'POST', body: cloudData }
        );
        const json = await cloudRes.json();
        if (!json.secure_url) {
          throw new Error('Cloudinary upload failed');
        }
        imageUrl = json.secure_url;
      } catch (err) {
        setError('Failed to upload profile picture.');
        setIsSubmitting(false);
        return;
      }
    }

    // Build payload
    const payload = new FormData();
    payload.append('title', formData.title);
    payload.append('firstName', formData.firstName);
    payload.append('middleName', formData.middleName);
    payload.append('lastName', formData.lastName);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('alternative_phone', formData.alternative_phone);
    payload.append('emergency_phone', formData.emergency_phone);
    payload.append('current_address', formData.current_address);
    payload.append('qualification', formData.qualification);
    payload.append('experience', formData.experience);
    if (formData.password) payload.append('password', formData.password);
    if (imageUrl) payload.append('profile_picture', imageUrl);

    try {
      // const res = await fetch(`${API_BASE_URL}/api/web/staff/update-profile/${userInfo.uuid}`, {
      const res = await fetch(`${API_BASE_URL}/api/web/staff/update-profile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: payload,
      });
      const result = await res.json();
      if (result.success) {
        alert('Profile updated successfully!');
        navigate('/teachers');
      } else {
        setError(result.message || 'Failed to update profile.');
      }
    } catch (err) {
      setError('Error updating profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">
              {`${formData.title || ''} ${formData.lastName || ''} ${formData.firstName || ''} ${formData.middleName || ''}`.trim() || 'Edit Profile'}
            </h1>
            <p className="text-blue-100">Edit your profile details</p>
          </div>
          <Link
            to="/teachers"
            className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5" />
            <span>Back to Teachers</span>
          </Link>
        </div>
      </div>

      {/* Form Container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        {error && <div className="text-red-600 text-center mb-4">{error}</div>}
        <form onSubmit={handleSubmit}>
          {/* Profile Picture Section */}
          <div className="mb-8 flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Profile preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaUpload className="text-gray-400 text-3xl" />
                )}
              </div>
              <label
                htmlFor="profilePicture"
                className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
              >
                <FaUpload className="text-sm" />
              </label>
              <input
                type="file"
                id="profilePicture"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePictureChange}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Upload profile picture (max 2MB)</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title *</label>
              <select
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select title</option>
                {titleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Middle Name</label>
              <input
                type="text"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alternative Phone Number</label>
              <input
                type="tel"
                name="alternative_phone"
                value={formData.alternative_phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Emergency Phone Number</label>
              <input
                type="tel"
                name="emergency_phone"
                value={formData.emergency_phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Address</label>
              <input
                type="text"
                name="current_address"
                value={formData.current_address}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Qualifications *</label>
              <textarea
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-h-[120px]"
                required
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teaching Experience *</label>
              <textarea
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white min-h-[120px]"
                required
              />
            </div>

            <div className="form-group col-span-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter new password (optional)"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <Link
              to="/teachers"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FaTimes />
              Cancel
            </Link>
            <button
              disabled={isSubmitting}
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <ButtonSpinner size="small" color="white" />
                  Updating...
                </>
              ) : (
                <>
                  <FaSave />
                  Update Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;